import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiHeart, FiCoffee, FiExternalLink, FiCheck } from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';

const DonationModal = ({ onClose }) => {
  const { t } = useLanguage();
  const [selectedType, setSelectedType] = useState('onetime');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // 🔥 Stripe 支付連結配置
  const STRIPE_LINKS = {
    monthly: {
      5: 'https://buy.stripe.com/00w14oeXhftjddZfX40Fi0Q',
      8: 'https://buy.stripe.com/9B6fZi16repf8XJ4em0Fi0R',
      10: 'https://buy.stripe.com/bJe28sdTd3KB6PB4em0Fi0S'
    },
    onetime: {
      5: 'https://buy.stripe.com/9B628s8yT80R4HteT00Fi0T',
      8: 'https://buy.stripe.com/bJe8wQ02n94V6PB9yG0Fi0U',
      10: 'https://buy.stripe.com/00wbJ2bL594V0rdbGO0Fi0V'
    }
  };

  const amounts = [
    { value: 5, label: '$5' },
    { value: 8, label: '$8' },
    { value: 10, label: '$10' }
  ];

  const handleDonate = () => {
    if (!selectedAmount) return;

    const stripeUrl = STRIPE_LINKS[selectedType][selectedAmount];

    if (stripeUrl) {
      // 🔥 顯示處理中狀態
      setPaymentProcessing(true);

      // 🔥 在新視窗開啟 Stripe 支付頁面
      window.open(stripeUrl, '_blank', 'noopener,noreferrer');

      // 🔥 3秒後重置狀態 (保留 Modal 開啟)
      setTimeout(() => {
        setPaymentProcessing(false);
      }, 3000);
    } else {
      alert('支付連結配置錯誤,請聯繫管理員');
    }
  };

  return (
    <div className="donation-modal-optimized">
      {/* 🔥 固定頂部 Header */}
      <div className="donation-header-fixed">
        <div>
          <h3 className="donation-title">
            <SafeIcon icon={FiHeart} />
            {t('donation.title')}
          </h3>
          <p className="donation-subtitle">{t('donation.subtitle')}</p>
        </div>
        <button
          onClick={onClose}
          className="donation-close-btn"
          aria-label={t('common.close')}
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </div>

      {/* 🔥 內容區域 - 無需滾動 */}
      <div className="donation-body-compact">
        {/* 🔥 類型選擇 - 橫向佈局 */}
        <div className="donation-type-section">
          <div className="donation-type-grid">
            <motion.button
              className={`donation-type-card ${selectedType === 'onetime' ? 'active' : ''}`}
              onClick={() => setSelectedType('onetime')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiCoffee} className="type-icon" />
              <div className="type-name">{t('donation.oneTime')}</div>
            </motion.button>

            <motion.button
              className={`donation-type-card ${selectedType === 'monthly' ? 'active' : ''}`}
              onClick={() => setSelectedType('monthly')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiHeart} className="type-icon" />
              <div className="type-name">{t('donation.monthly')}</div>
            </motion.button>
          </div>
        </div>

        {/* 🔥 金額選擇 - 大按鈕設計 */}
        <div className="donation-amount-section">
          <div className="donation-amount-grid">
            {amounts.map((amount) => (
              <motion.button
                key={amount.value}
                className={`donation-amount-card ${selectedAmount === amount.value ? 'active' : ''}`}
                onClick={() => setSelectedAmount(amount.value)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="amount-value">{amount.label}</div>
                {selectedType === 'monthly' && (
                  <div className="amount-period">{t('donation.perMonth')}</div>
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* 🔥 支付方式說明 - 簡化版 */}
        <div className="donation-payment-info">
          <div className="payment-info-icon">💳</div>
          <div className="payment-info-text">
            <div className="payment-info-title">
              {t('donation.paymentMethod') || '支付方式'}
            </div>
            <div className="payment-info-desc">
              {t('donation.securePayment') || '安全的 Stripe 支付系統'}
            </div>
          </div>
        </div>
      </div>

      {/* 🔥 固定底部按鈕 */}
      <div className="donation-footer-fixed">
        <AnimatePresence mode="wait">
          {paymentProcessing ? (
            <motion.div
              key="processing"
              className="donation-processing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <SafeIcon icon={FiCheck} className="processing-icon" />
              <span>{t('donation.paymentPageOpened')}</span>
            </motion.div>
          ) : (
            <motion.button
              key="donate"
              className={`donation-submit-optimized ${!selectedAmount ? 'disabled' : ''}`}
              onClick={handleDonate}
              disabled={!selectedAmount}
              whileHover={selectedAmount ? { scale: 1.02 } : {}}
              whileTap={selectedAmount ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {selectedAmount ? (
                <div className="submit-content">
                  <span className="submit-text">
                    {t('donation.donateButton')} ${selectedAmount}
                    {selectedType === 'monthly' && ` ${t('donation.perMonth')}`}
                  </span>
                  <SafeIcon icon={FiExternalLink} className="submit-icon" />
                </div>
              ) : (
                <span className="submit-text-disabled">
                  {t('donation.selectAmountFirst')}
                </span>
              )}
            </motion.button>
          )}
        </AnimatePresence>

        {/* 🔥 底部提示 */}
        <div className="donation-footer-note">
          <span>🔒</span>
          <span>{t('donation.poweredByStripe') || 'Powered by Stripe - 安全可靠'}</span>
        </div>
      </div>
    </div>
  );
};

export default DonationModal;